import { Injectable } from '@angular/core';
import { AngularFireDatabase } from 'angularfire2/database';
import { HttpClient } from '@angular/common/http';
import { ENV } from '@app/env';
import { IUser } from 'models/user.model';
import { Observable } from 'rxjs/Observable';
import { UtilitiesProvider } from './utilities';
import { TrackingData } from './tracking';

@Injectable()
export class StripeProvider {

  constructor(
    private afDatabase: AngularFireDatabase,
    private http: HttpClient,
    private utilities: UtilitiesProvider,
    private tracking: TrackingData
  ) { }

  /**
   * Retrieve customer data
   * @param customer_id current customer id to be retrieved
   * @author Jhonny Martínez
   */
  getCustomer(customer_id: string) {
    return this.http.get(`${ENV.pay_api_url}/customer/${customer_id}`);
  }

  /**
   * Create a new customer with default card token
   * @param clientId current client id
   * @param userId current user id
   * @param userEmail current user email
   * @param token default card token for the new customer
   * @author Jhonny Martínez
   */
  addCustomer(userId: string, userEmail: string, token: string) {
    return this.http.post(`${ENV.pay_api_url}/customer/create`, {
      email: userEmail,
      uid: userId,
      token
    });
  }

  /**
   * Update customer data
   * @param customer_id current user customer id
   * @param data new customer data
   * @author Jhonny Martínez
   */
  updateCustomer(customer_id: string, data: any) {
    return this.http.post(`${ENV.pay_api_url}/customer/update/${customer_id}`, data);
  }

  /**
   * Create new card for a customer
   * @param customer_id current customer id
   * @param token card token to be added
   * @author Jhonny Martínez
   */
  addCard(customer_id: string, token: string) {
    return this.http.post(`${ENV.pay_api_url}/card/create`, {
      customer_id,
      token
    });
  }

  /**
   * Delete credit card from customer
   * @param customer_id current customer id
   * @param card_id card id to be deleted
   * @author Jhonny Martínez
   */
  deleteCard(customer_id: string, card_id: string) {
    return this.http.post(`${ENV.pay_api_url}/card/delete`, {
      customer_id,
      card_id
    });
  }

  /**
   * Save customer id to current user in database
   * @param clientId current client id
   * @param userId current user id
   * @param customer_id current user customer id
   * @author Jhonny Martínez
   */
  saveCustomerId(clientId: string, userId: string, customer_id: string) {
    return this.afDatabase.object(`/Clients/${clientId}/Users/${userId}/customer_id`)
      .set(customer_id);
  }

  /**
   * get user's customer card by id or default card
   * @param customer_id current user customer id
   * @param card_id card id to be retrieved
   * @author Jhonny Martínez
   */
  getCustomerCard(customer_id: string, card_id?: string) {
    if (card_id) {
      return this.http.get(`${ENV.pay_api_url}/customer/${customer_id}/card/${card_id}`);
    } else {
      return this.http.get(`${ENV.pay_api_url}/customer/${customer_id}/card`);
    }
  }

  chargeReserve(clientId: string, user: IUser, reserve: any, currency: string) {
    const key = this.afDatabase.createPushId();
    const description = `Reserve transaction with reserve id: ${key}`;
    const amount = (parseInt(reserve.price) * 100);
    return this.http.post(`${ENV.pay_api_url}/charge/create`, {
      amount,
      currency,
      customer_id: user.info.customer_id,
      description
    }).switchMap((res: any) => {
      this.utilities.consoleDecorator('Transaction res: ', res);
      const promise1 = this.afDatabase.object(`/Clients/${clientId}/UserPaymentHistory/${user.info.id}/${key}`)
        .set({ transaction: res, summary: { ...reserve, state: res.status }, id: key });
      const promise2 = this.afDatabase.object(`/Clients/${clientId}/UserReserves/${user.info.id}/${reserve.id}`)
        .set({ ...reserve, status: res.status, id: reserve.id });
      return Observable.fromPromise(
        Promise.all([promise1, promise2])
      ).map(() => res);
    })
  }

  charge(clientId: string, user: IUser, total: number, currency: string, cart: any) {
    const key = this.afDatabase.createPushId();
    const description = `Catalog payment with transaction id: ${key}`;
    const amount = total * 100;
    return this.http.post(`${ENV.pay_api_url}/charge/create`, {
      amount,
      currency,
      customer_id: user.info.customer_id,
      description
    }).switchMap((res: any) => {
      const promise = this.afDatabase.object(`/Clients/${clientId}/UserPaymentHistory/${user.info.id}/${key}`)
        .set({ transaction: res, summary: { ...cart, status: res.status }, id: key })
        .then(() => {
          // Se llama el servicio de AI donde se incluye la compra del usuario
          // TODO: Se debe verificar si se envia siempre a AI o solo cuando el resultado de la transaccion sea success o pending
          const randomId = this.afDatabase.createPushId();
          const payload = {
            ...cart,
            status: res.status,
            id: randomId,
            clientId: clientId,
            userId: user.info.id
          }
          this.tracking.trackEntity('shopping_carts', randomId, clientId, payload, 'create');

          return this.afDatabase.object(`/Clients/${clientId}/UserShoppingCart/${user.info.id}`).set(null)
        })
        .then(() => {
          return Promise.all(cart.products.map(product => {
            return this.afDatabase.object(`/Clients/${clientId}/Catalogs/${product.id}/stock`)
              .query.ref.transaction(val => ((parseInt(val) - 1) + ''))
          })).then(() => res);
        });
      return Observable.fromPromise(promise);
    });

  }

}


// WEBPACK FOOTER //
// ./src/providers/stripe.ts